import type { Pinia } from "pinia";
import { storeToRefs } from "pinia";
import type { NavigationGuardNext, RouteLocationNormalized, RouteLocationNormalizedLoaded } from "vue-router";

import * as tmi from "@/constants";
import { forbiddenQuery } from "@/router/forbidden-location";
import routeManager from "@/router/route-manager";
import { useApplicationStore } from "@/stores/Application";
import { useAuthStore } from "@/stores/Auth";
import { usePermissionsStore } from "@/stores/Permissions";

/**
 * The sign-in, sign-up and password-reset pages: the round trip a stashed 'nextLinkAfterLogin' is for. Names, so the
 * sign-in form's ?reason= notice and the token routes' parameters match too.
 */
const AuthFlowRouteNames: ReadonlySet< unknown > = new Set( [
    tmi.SignInName,
    tmi.SignInAsUserName,
    tmi.SignUpName,
    tmi.ResetPasswordName,
    tmi.NewPasswordName
] );

function isAuthFlowRoute( to: RouteLocationNormalized ): boolean {
    return AuthFlowRouteNames.has( to.name );
}

/**
 * The single auth gate — registered as `router.beforeResolve( createAuthGuard( pinia ) )`.
 * Extracted from router/index.ts (tmi-6gh, LD6) so specs can drive it directly with fake
 * `to` / `next` and real store modules instead of standing up a router.
 *
 * Route contract: `meta.public` skips auth entirely; otherwise the route is authenticated and
 * `meta.permission` (feature key) plus optional `meta.shortCode` (product tier) must both pass.
 * NOTE (R48): `hasFeatureAccess( '' )` is always false, so an authenticated route that omits
 * `meta.permission` 403s for everyone — every non-public route must declare one.
 */
export function createAuthGuard( pinia: Pinia ) {
    return async ( to: RouteLocationNormalized, _: RouteLocationNormalizedLoaded, next: NavigationGuardNext ): Promise<void> => {
        const { isAuthenticated } = storeToRefs( useAuthStore( pinia ) );
        // Never clear 'nextLinkAfterLogin' on the way to the sign-in, sign-up or password-reset pages. The
        // redirect to sign-in below is itself a navigation that re-enters this guard, so a clear on every
        // signed-out pass erased the return path it had just stored and every member landed on the
        // Dashboard after signing in. The key is consumed (and cleared) by
        // routeManager.handleRedirectWithOptionalExplicitPath.
        // A signed-out visitor who leaves that round trip for any other public page has abandoned it, though:
        // kept, the stale path would take over a later sign-in from the header (and land a member whose plan
        // lacks it on the 403 page). Every writer stashes the key right before going to one of those pages.
        if ( !isAuthenticated.value && to.meta?.public && !isAuthFlowRoute( to ) ) {
            sessionStorage.removeItem( 'nextLinkAfterLogin' );
        }

        // A signed-in member has nothing to do on the sign-in form (a stale bookmark, a header link
        // in another tab). The token route (SignInAsUserName) stays reachable: it switches users.
        if ( isAuthenticated.value && to.name === tmi.SignInName ) {
            next( { path: routeManager.homePath() } );
            return;
        }

        const routeRequiresAuthentication: boolean = !to.meta?.public;
        const routeRequiresSpecificPermissions: boolean = !!( to.meta?.permission );

        // The selected route requires authorization
        if ( routeRequiresAuthentication || routeRequiresSpecificPermissions ) {
            // The following series of predicates will fire in the situation where there exists a valid authentication token
            // in localstorage, but the permissions have not been loaded/re-loaded into memory. This can happen in multiple scenarios:
            //      1) The user manually edits the url in the browser
            //      2) The user refreshes the browser
            //      3) A new tab/window is opened during the time window when the authentication token is still valid
            //      4) The cache was purged (or never filled) while the token survived — e.g. a PWA update reload mid-login

            // Divert the user to the sign-in page b/c they are requesting a route that requires authentication and they are not authenticated
            if ( !isAuthenticated.value ) {
                // fullPath, not path: an alert link's ?tab= / #anchor must survive the round trip.
                sessionStorage.setItem( 'nextLinkAfterLogin', to?.fullPath || to?.path || routeManager.homePath() );
                next( { name: tmi.SignInName } );
                return;
            }

            const permissionsStore = usePermissionsStore( pinia );
            // UX roadmap #50: false only when the grants could not be loaded at all. Not `isInitialized`: a member
            // with zero grants heals to true and stays uninitialized, and their 403 is a real "not in your plan".
            let healed: boolean = true;
            if ( !permissionsStore.isInitialized ) {
                // Self-heal (LD2): localStorage first, then profile + grants from the server.
                // Never rejects; a failure falls through to the 403 path below.
                healed = await permissionsStore.ensureInitializedAsync();
                // Start the permission-gated stores now that the grants exist (R45: a later navigation's heal must
                // still reach them), but never wait for them. main.ts mounts only after the first navigation
                // resolves, so awaiting the whole data boot here left a member with a token but no cached grants on
                // a blank page until it finished. App.vue's bootstrapShell() joins this run (UX roadmap #9).
                if ( healed ) {
                    void useApplicationStore( pinia ).initializeAsync();
                }
            }

            // User is authenticated, validate that they have the correct permissions
            const metaPermission: string = `${ to.meta?.permission ?? '' }`;
            let isAuthorizedForRoute: boolean = permissionsStore.hasFeatureAccess( metaPermission );

            // Some routes are additionally gated by a product short code (e.g. 'SSS'
            // for the DMI alerts feed). meta.permission is single-valued and
            // feature-based, so the short code rides in its own optional field.
            // Exclusive tiers (like SSS) are filtered server-side out of the
            // alliance/fullaccess umbrella expansion, so a positive short-code
            // match is proof of a direct grant — no exclusion list is needed here.
            const metaShortCode: string = `${ to.meta?.shortCode ?? '' }`;
            if ( isAuthorizedForRoute && metaShortCode ) {
                isAuthorizedForRoute = permissionsStore.hasAccessBasedOnShortCode( metaShortCode );
            }

            // user is NOT authorized for route
            if ( !isAuthorizedForRoute ) {
                // UX roadmap #50: the 403 page is told what was refused and why, so it can name the page and the
                // product that includes it, or offer a retry when access could not be checked. It used to get
                // nothing and told every member their session may have expired.
                next( {
                    path: routeManager.forbiddenPath(),
                    query: forbiddenQuery( {
                        from: to.fullPath || to.path,
                        need: metaPermission,
                        code: metaShortCode,
                        reason: healed ? 'locked' : 'unverified',
                    } ),
                } );
                return;
            }
        }

        // The selected route is not protected
        next();
    };
}
